import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Calendar, 
  ShieldCheck, 
  Zap, 
  Headphones, 
  Trophy, 
  Briefcase, 
  GraduationCap,
  Plane,
  MapPin,
  Clock,
  CheckCircle2
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiciosEmpresas = () => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/pages/servicios-empresas?t=${Date.now()}`);
        setPageData(res.data);
      } catch (err) {
        console.error('Error fetching page content:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.seo_title || "Empresas & Colectivos | Viajes de Ensueño";
      const description = pageData.seo_description || "Gestionamos la movilidad de vuestro éxito.";
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    }
  }, [pageData]);

  const handleContactClick = () => {
    navigate('/contacto?subject=Contacto de Empresa/Colectivo');
  };

  const corporateFeatures = [
    {
      icon: <ShieldCheck className="w-8 h-8 text-blue-600" />,
      title: "Gestión Integral",
      description: "Nos encargamos de todo el proceso para que tu equipo se despreocupe. Desde el vuelo más complejo hasta el traslado más sencillo."
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Autorizaciones Internas",
      description: "Implementamos procesos de validación personalizados para que cada viaje cumpla con las políticas de tu empresa u organización."
    },
    {
      icon: <Headphones className="w-8 h-8 text-blue-600" />,
      title: "Atención 24/7",
      description: "Soporte constante para cualquier imprevisto durante el viaje, garantizando la seguridad y tranquilidad del viajero."
    },
    {
      icon: <Building2 className="w-8 h-8 text-blue-600" />,
      title: "Servicio Implant",
      description: "Para grandes cuentas, ofrecemos la posibilidad de desplazar personal especializado directamente a vuestras instalaciones (In-house)."
    }
  ];

  const sportsFocus = [
    {
      title: "Logística Deportiva",
      description: "Gestión de traslados para equipos completos, transporte de material deportivo especial y reserva de alojamientos con dietas adaptadas para atletas.",
      icon: <Trophy className="w-6 h-6" />
    },
    {
      title: "Competiciones Internacionales",
      description: "Tramitación de visados en grupo, seguros de competición y coordinación en destino para torneos y stages de pretemporada.",
      icon: <Plane className="w-6 h-6" />
    }
  ];

  const categories = [
    {
      title: "Empresas & Pymes",
      icon: <Briefcase className="w-10 h-10 mb-4 text-blue-600" />,
      items: ["Viajes de negocios", "Incentivos", "Convenciones anuales", "Políticas de viaje"]
    },
    {
      title: "Clubes & Federaciones",
      icon: <Trophy className="w-10 h-10 mb-4 text-blue-600" />,
      items: ["Torneos nacionales", "Giras internacionales", "Gestión de material", "Viajes de directivas"]
    },
    {
      title: "Asociaciones & ONG",
      icon: <Users className="w-10 h-10 mb-4 text-blue-600" />,
      items: ["Congresos", "Viajes culturales", "Misiones internacionales", "Tarifas de grupo"]
    },
    {
      title: "Instituciones",
      icon: <GraduationCap className="w-10 h-10 mb-4 text-blue-600" />,
      items: ["Viajes académicos", "Intercambios", "Protocolo", "Presupuestos oficiales"]
    }
  ];

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        {/* Header con Imagen */}
        <div className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000" 
              alt="Business Travel" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-blue-900/70 mix-blend-multiply"></div>
          </div>
          
          <div className="relative z-10 max-w-5xl mx-auto text-center px-4 pt-10">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Diseñamos la Movilidad de <span className="text-blue-300">Empresas & Colectivos</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-50 max-w-3xl mx-auto mb-10 leading-relaxed font-light">
              Desde viajes corporativos de alta dirección hasta la logística integral de clubes deportivos y federaciones.
            </p>
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 px-10 py-7 text-lg rounded-full font-bold shadow-2xl" onClick={handleContactClick}>
              Contactar Area Empresas
            </Button>
          </div>
        </div>

        {/* Agencia Corporativa Section */}
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Especialistas en Business Travel</h2>
              <div className="w-20 h-1.5 bg-blue-600 mx-auto rounded-full"></div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {corporateFeatures.map((feature, index) => (
                <Card key={index} className="border-none shadow-sm hover:shadow-lg transition-all duration-300 bg-white">
                  <CardContent className="pt-8 text-center lg:text-left">
                    <div className="mb-5 inline-block p-3 bg-blue-50 rounded-2xl">{feature.icon}</div>
                    <h3 className="text-xl font-bold mb-3 text-slate-800">{feature.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Sports Clubs Focus - NEW SECTION WITH IMAGE */}
        <div className="py-12 px-4 bg-white">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Trophy className="text-blue-600 w-8 h-8" />
                Expertos en Logística Deportiva
              </h2>
              <p className="text-slate-600 mb-8 text-lg">
                Entendemos las necesidades críticas de los clubes y federaciones. La puntualidad, el descanso y la logística son factores decisivos en el rendimiento deportivo.
              </p>
              <div className="space-y-4">
                {sportsFocus.map((item, i) => (
                  <div key={i} className="flex gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100">
                    <div className="text-blue-600 mt-1">{item.icon}</div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-500">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?auto=format&fit=crop&q=80&w=1200" 
                alt="Logística deportiva" 
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Events Section - REDUCED SPACING */}
        <div className="py-12 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80&w=1200" 
                  alt="Eventos corporativos" 
                  className="rounded-2xl shadow-xl"
                />
              </div>
              <div className="md:w-1/2">
                <div className="inline-block p-3 rounded-2xl bg-blue-600 text-white mb-6">
                  <Calendar className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Eventos & MICE</h2>
                <div className="space-y-4 text-slate-600">
                  <p className="text-lg">Desde una reunión de comité de dirección hasta una convención internacional. Nos ocupamos de cada detalle técnico y logístico.</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {["Team Building", "Convenciones", "Viajes de Incentivo", "Lanzamientos", "Workshops", "Congresos"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Colectivos Section */}
        <div className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Soluciones para Colectivos</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Adaptamos nuestro servicio a la naturaleza de vuestra organización, garantizando eficiencia y optimización de presupuesto.</p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-3xl border border-slate-100 hover:border-blue-200 transition-colors flex flex-col items-center text-center group">
                <div className="transform group-hover:scale-110 transition-transform duration-300">
                  {cat.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-slate-800">{cat.title}</h3>
                <ul className="text-slate-500 text-xs space-y-2 uppercase tracking-wider font-semibold">
                  {cat.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Final Image & CTA */}
        <div className="relative py-24 px-4 overflow-hidden">
          <div className="absolute inset-0">
            <img 
               src="https://images.unsplash.com/photo-1436450412740-6b988f486c6b?auto=format&fit=crop&q=80&w=2000" 
               alt="Footer Background" 
               className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-blue-600/90"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-6 italic font-serif leading-tight">"Su única preocupación será el éxito de su organización"</h2>
            <p className="text-xl text-blue-100 mb-10">
              Disponemos de equipos especializados para atender cada petición de forma individualizada.
            </p>
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-12 py-8 text-xl rounded-full font-bold shadow-xl shadow-black/20" onClick={handleContactClick}>
              Contactar Area Empresas & Colectivos
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ServiciosEmpresas;
